import { cva, type VariantProps } from 'class-variance-authority'
import { useMemo } from 'react'
import { cn } from '#app/utils/misc.tsx'
import { Label } from './label.tsx'
import { Separator } from './separator.tsx'

function FieldSet({ className, ...props }: React.ComponentProps<'fieldset'>) {
	return (
		<fieldset
			data-slot="field-set"
			className={cn(
				'flex flex-col gap-6',
				'has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3',
				className,
			)}
			{...props}
		/>
	)
}

function FieldLegend({
	className,
	variant = 'legend',
	...props
}: React.ComponentProps<'legend'> & { variant?: 'legend' | 'label' }) {
	return (
		<legend
			data-slot="field-legend"
			data-variant={variant}
			className={cn(
				'mb-3 font-medium text-white',
				'data-[variant=legend]:text-base',
				'data-[variant=label]:text-sm',
				className,
			)}
			{...props}
		/>
	)
}

function FieldGroup({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="field-group"
			className={cn(
				'group/field-group @container/field-group flex w-full flex-col gap-6 data-[slot=checkbox-group]:gap-3 [&>[data-slot=field-group]]:gap-4',
				className,
			)}
			{...props}
		/>
	)
}

const fieldVariants = cva(
	'group/field flex w-full gap-2 data-[invalid=true]:text-foreground-destructive',
	{
		variants: {
			orientation: {
				vertical: ['flex-col [&>*]:w-full [&>.sr-only]:w-auto'],
				horizontal: [
					'flex-row items-center',
					'[&>[data-slot=field-label]]:flex-auto',
				],
				responsive: [
					'flex-col @md/field-group:flex-row @md/field-group:items-center [&>*]:w-full @md/field-group:[&>*]:w-auto [&>.sr-only]:w-auto',
					'@md/field-group:[&>[data-slot=field-label]]:flex-auto',
				],
			},
		},
		defaultVariants: { orientation: 'vertical' },
	},
)

function Field({
	className,
	orientation = 'vertical',
	...props
}: React.ComponentProps<'div'> & VariantProps<typeof fieldVariants>) {
	return (
		<div
			role="group"
			data-slot="field"
			data-orientation={orientation}
			className={cn(fieldVariants({ orientation }), className)}
			{...props}
		/>
	)
}

function FieldContent({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="field-content"
			className={cn(
				'group/field-content flex flex-1 flex-col gap-1.5 leading-snug',
				className,
			)}
			{...props}
		/>
	)
}

function FieldLabel({
	className,
	...props
}: React.ComponentProps<typeof Label>) {
	return (
		<Label
			data-slot="field-label"
			className={cn(
				'group/field-label peer/field-label text-text-primary flex w-fit gap-2 text-sm leading-snug font-medium',
				className,
			)}
			{...props}
		/>
	)
}

function FieldDescription({ className, ...props }: React.ComponentProps<'p'>) {
	return (
		<p
			data-slot="field-description"
			className={cn(
				'text-text-muted text-xs leading-normal font-normal',
				'[&>a]:text-primary [&>a]:underline [&>a]:underline-offset-4',
				className,
			)}
			{...props}
		/>
	)
}

function FieldSeparator({
	children,
	className,
	...props
}: React.ComponentProps<'div'> & { children?: React.ReactNode }) {
	return (
		<div
			data-slot="field-separator"
			data-content={!!children}
			className={cn('relative -my-1 h-5 text-xs', className)}
			{...props}
		>
			<Separator className="absolute inset-0 top-1/2" />
			{children ? (
				<span
					className="text-text-muted relative mx-auto block w-fit bg-[#18181b] px-2 uppercase tracking-wider"
					data-slot="field-separator-content"
				>
					{children}
				</span>
			) : null}
		</div>
	)
}

function FieldError({
	className,
	children,
	errors,
	...props
}: React.ComponentProps<'div'> & {
	errors?: Array<{ message?: string } | undefined>
}) {
	const content = useMemo(() => {
		if (children) return children
		if (!errors?.length) return null
		const unique = [
			...new Map(errors.map((e) => [e?.message, e])).values(),
		].filter((e): e is { message?: string } => Boolean(e?.message))
		if (unique.length === 1) return unique[0]?.message
		return (
			<ul className="ml-4 flex list-disc flex-col gap-1">
				{unique.map((e, i) => (
					<li key={i}>{e.message}</li>
				))}
			</ul>
		)
	}, [children, errors])

	if (!content) return null

	return (
		<div
			role="alert"
			data-slot="field-error"
			className={cn('text-foreground-destructive text-xs', className)}
			{...props}
		>
			{content}
		</div>
	)
}

export {
	Field,
	FieldContent,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel,
	FieldLegend,
	FieldSeparator,
	FieldSet,
}
